import React, { useState, useMemo, useEffect, useRef } from "react";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { format } from "date-fns";
import { Box } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { IoIosArrowBack } from "react-icons/io";
import {
  useFetchInvoiceProjectsQuery,
  useFetchInvoicesQueueQuery,
  useFetchPreviousInvoicesMutation,
  useFixedCostMapInvoiceMutation,
  useFetchProjectResourcesMutation,
} from "../../../slice/logdetailSlice";
import { CustomNoRowsOverlay } from "../../../components/common/OverlayGrid";
import { useTheme } from "../../../ThemeContext";
import { buildExistingProjectsMap } from "../../../utils/budgetTypeUtils";
import ProjectSelectAutocomplete from "../../../components/common/ProjectSelectAutocomplete";

/**
 * Screen 3: Fixed Cost — Scope & Change Request Mapping Modal
 * Built to specifications from Mockup Screen 3 & HTML 2 (CR Runs).
 * Maps incoming fixed-cost receipts to a Project and Scope (Base Scope vs CRs).
 */
const FixedCostMappingModal = ({
  show,
  onHide,
  invoice,
  existingInvoices = [],
  onSaveMapping,
  onOpenFinalSplit,
  onSwitchToContract,
  isInline = false,
}) => {
  const { isDarkMode } = useTheme();
  const { data: projectsResponse } = useFetchInvoiceProjectsQuery();
  const projectList = useMemo(() => projectsResponse?.data || [], [projectsResponse]);

  // Fetch fresh invoices queue to populate project status badges (Allocated, Mapped, etc.)
  const { data: queueResponse } = useFetchInvoicesQueueQuery({}, { skip: !show && !isInline });

  // Map of project IDs and project names that already have an invoice record in queue
  const existingProjectsMap = useMemo(() => {
    return buildExistingProjectsMap(
      existingInvoices,
      queueResponse?.data,
      projectList
    );
  }, [existingInvoices, queueResponse, projectList]);

  const fixedProjects = useMemo(() => {
    if (!Array.isArray(projectList)) return [];
    return projectList.filter(
      (p) =>
        p.budget_type?.toUpperCase() === "FIXED_COST" ||
        p.budget_type?.toUpperCase() === "FIXED"
    );
  }, [projectList]);

  const [mapScope, setMapScope] = useState("Base scope");
  // Store selected project ID instead of name for easier API calls
  const [selectedProject, setSelectedProject] = useState("");
  // Store fetched previous invoices and summary
  const [prevInvoices, setPrevInvoices] = useState([]);
  const [prevInvoicesSummary, setPrevInvoicesSummary] = useState(null);
  const [fetchPreviousInvoices, fetchState] = useFetchPreviousInvoicesMutation();
  const [fixedCostMapInvoice, { isLoading: isMappingFixed }] = useFixedCostMapInvoiceMutation();
  const [fetchProjectResources] = useFetchProjectResourcesMutation();
  const [projectCostToDate, setProjectCostToDate] = useState(0);
  const lastFetchedProjectIdRef = useRef("");

  // Fetch previous invoices whenever a project is selected
  useEffect(() => {
    if (!show && !isInline) return;
    if (selectedProject) {
      const projKey = String(selectedProject);
      if (lastFetchedProjectIdRef.current === projKey) {
        return;
      }
      lastFetchedProjectIdRef.current = projKey;

      fetchPreviousInvoices({ project_id: Number(selectedProject) || selectedProject })
        .unwrap()
        .then((res) => {
          // Expected response shape: { data: [...], summary: {...} }
          if (res && res.data) {
            setPrevInvoices(res.data);
            setPrevInvoicesSummary(res.summary || null);
          } else if (Array.isArray(res)) {
            setPrevInvoices(res);
          }
        })
        .catch(() => {
          toast.error('Failed to fetch previous invoices');
          lastFetchedProjectIdRef.current = "";
        });

      fetchProjectResources({ project_id: Number(selectedProject) || selectedProject })
        .unwrap()
        .then((res) => {
          if (res?.total_cost != null) {
            setProjectCostToDate(Number(res.total_cost));
          } else if (res?.data && Array.isArray(res.data)) {
            const sumCost = res.data.reduce((acc, r) => acc + (Number(r.cost) || 0), 0);
            setProjectCostToDate(sumCost);
          }
        })
        .catch(() => {
          setProjectCostToDate(0);
        });
    } else {
      lastFetchedProjectIdRef.current = "";
      setPrevInvoices([]);
      setPrevInvoicesSummary(null);
      setProjectCostToDate(0);
    }
  }, [show, isInline, selectedProject, fetchPreviousInvoices, fetchProjectResources]);

  useEffect(() => {
    if ((show || isInline) && invoice) {
      // Initialize with existing project ID if available
      const rawProjId = invoice.project_id || invoice.rawItem?.project_id || "";
      const existingProjId = rawProjId && rawProjId !== "—" && rawProjId !== "-" ? String(rawProjId) : "";
      setSelectedProject(existingProjId);
      setMapScope("Base scope");
    } else if (!show && !isInline) {
      setSelectedProject("");
      setPrevInvoices([]);
      setMapScope("Base scope");
    }
  }, [show, isInline, invoice?.id, invoice?.rawItem?.invoice_id]);

  const isAllocated = invoice?.status?.toLowerCase() === "allocated";
  const isMapped = invoice?.status?.toLowerCase() === "mapped";
  const isManual =
    invoice?.source === "MANUAL" ||
    invoice?.source === "MANINV" ||
    invoice?.source === "CASH" ||
    String(invoice?.invoice || invoice?.invoice_number || "").startsWith("MAN");
  const isProjectLocked = Boolean(isAllocated || isManual || (selectedProject && isMapped));
  const matchedProjectObj =
    fixedProjects.find(
      (p) => String(p.project_id) === String(selectedProject) || String(p.id) === String(selectedProject)
    ) ||
    projectList.find(
      (p) => String(p.project_id) === String(selectedProject) || String(p.id) === String(selectedProject)
    );

  const handleProjectSelect = async (newProjId) => {
    if (!newProjId) {
      setSelectedProject("");
      return;
    }

    const chosen = (fixedProjects.length > 0 ? fixedProjects : projectList).find(
      (p) =>
        String(p.project_id) === String(newProjId) ||
        String(p.id) === String(newProjId) ||
        p.title === newProjId ||
        p.project_name === newProjId
    );

    // Client mismatch check
    const currentInvoiceClient = (
      invoice?.client ||
      invoice?.client_name ||
      invoice?.rawItem?.client_name ||
      ""
    ).trim();

    const existingMappedInv = (existingInvoices || [])
      .concat(queueResponse?.data || [])
      .find((inv) => {
        const pId = inv.project_id || inv.rawItem?.project_id || inv.project?.project_id;
        const invClient = (inv.client_name || inv.client || inv.rawItem?.client_name || "").trim();
        return (
          String(pId) === String(newProjId) &&
          invClient &&
          invClient !== "—" &&
          invClient !== "-" &&
          invClient.toLowerCase() !== String(inv.project || inv.project_name || "").toLowerCase()
        );
      });

    const projectExistingClient = (
      chosen?.client_name ||
      existingMappedInv?.client_name ||
      existingMappedInv?.client ||
      existingMappedInv?.rawItem?.client_name ||
      ""
    ).trim();

    if (
      currentInvoiceClient &&
      currentInvoiceClient !== "—" &&
      currentInvoiceClient !== "-" &&
      projectExistingClient &&
      projectExistingClient !== "—" &&
      projectExistingClient !== "-" &&
      currentInvoiceClient.toLowerCase() !== projectExistingClient.toLowerCase()
    ) {
      const projTitle = chosen?.title || chosen?.project_name || chosen?.name || `Project #${newProjId}`;
      const invNum = invoice?.invoice || invoice?.invoice_number || invoice?.rawItem?.invoice_number || "";
      const invLabel = invNum ? `invoice <strong>"${invNum}"</strong> for ` : "";

      const result = await Swal.fire({
        title: "Client Mismatch Warning",
        html: `The selected project <strong>"${projTitle}"</strong> is already mapped to client <strong>"${projectExistingClient}"</strong>.<br/><br/>Do you want to map this project to ${invLabel}client <strong>"${currentInvoiceClient}"</strong>?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#10b981",
        cancelButtonColor: "#ef4444",
        confirmButtonText: "Yes, Map Project",
        cancelButtonText: "Cancel",
        reverseButtons: true,
      });

      if (!result.isConfirmed) {
        return;
      }
    }

    setSelectedProject(newProjId);
  };

  const fixedCostColumns = useMemo(
    () => [
      {
        field: "invoice",
        headerName: "Invoice",
        flex: 1.2,
        minWidth: 140,
        renderCell: (params) => (
          <span style={{ color: "#374151", fontSize: "13px" }}>
            {params.value}
          </span>
        ),
      },
      {
        field: "source",
        headerName: "Source",
        flex: 0.9,
        minWidth: 100,
        renderCell: (params) => {
          const src = params.value;
          if (src === "MANUAL" || src === "MANINV") {
            return <span className="badge-source-manual">MANUAL</span>;
          }
          return (
            <span className="badge bg-light text-dark border">
              {src || "TT"}
            </span>
          );
        },
      },
      {
        field: "date",
        headerName: "Invoice Date",
        flex: 1,
        minWidth: 120,
        renderCell: (params) => (
          <span style={{ color: "#374151", fontSize: "13px" }}>{params.value}</span>
        ),
      },
      {
        field: "payment_date",
        headerName: "Payment Received",
        flex: 1.2,
        minWidth: 160,
        renderCell: (params) => (
          <span style={{ color: "#374151", fontSize: "13px" }}>{params.value || "—"}</span>
        ),
      },
      {
        field: "amount",
        headerName: "Amount",
        width: 160,
        minWidth: 160,
        headerAlign: "right",
        align: "right",
        renderCell: (params) => (
          <span
            style={{
              color: "#374151",
              fontSize: "13px",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {params.value}
          </span>
        ),
      },
      {
        field: "status",
        headerName: "Status",
        width: 140,
        minWidth: 140,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => {
          if (params.row.isCurrent) {
            return (
              <span
                style={{
                  backgroundColor: "#06b6d4",
                  color: "#ffffff",
                  fontWeight: 500,
                  fontSize: "12px",
                  padding: "2px 12px",
                  height: "24px",
                  lineHeight: "24px",
                  borderRadius: "9999px",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  whiteSpace: "nowrap",
                }}
              >
                this one
              </span>
            );
          }
          return <span className="badge-status-mapped">Mapped</span>;
        },
      },
    ],
    []
  );

  const displayInvoiceDate = useMemo(() => {
    const rawDate =
      invoice?.invoiced_date ||
      invoice?.rawItem?.invoiced_date ||
      invoice?.created_at ||
      invoice?.date;
    if (!rawDate) return "—";
    try {
      const d = new Date(rawDate);
      return isNaN(d.getTime()) ? rawDate : format(d, "dd MMM yyyy");
    } catch {
      return rawDate;
    }
  }, [invoice]);

  const displayPaymentDate = useMemo(() => {
    const rawDate =
      invoice?.payment_date ||
      invoice?.rawItem?.payment_date;
    if (!rawDate) return "—";
    try {
      const d = new Date(rawDate);
      return isNaN(d.getTime()) ? rawDate : format(d, "dd MMM yyyy");
    } catch {
      return rawDate;
    }
  }, [invoice]);

  const displayAmount = useMemo(() => {
    const rawAmt =
      invoice?.total_amount ||
      invoice?.amount ||
      invoice?.rawItem?.total_amount;
    if (rawAmt === undefined || rawAmt === null || rawAmt === "") return "0.00";
    const num = Number(String(rawAmt).replace(/,/g, ""));
    if (isNaN(num)) return String(rawAmt);
    return num.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }, [invoice]);

  const fixedCostRows = useMemo(() => {
    const currentInvoiceNumber = invoice?.invoice || invoice?.invoice_number || "INV-002419";
    const rawSource = String(invoice?.source || invoice?.rawItem?.source || "").toUpperCase();
    const isManualSource = rawSource === "MANUAL" || rawSource === "MANINV" || rawSource === "CASH" || currentInvoiceNumber.startsWith("MAN");
    const currentSource = isManualSource ? "MANUAL" : (invoice?.source || "TT");
    const currentDate = displayInvoiceDate;
    const currentAmountFmt = displayAmount;
    const currentNumAmt = Number(String(invoice?.total_amount || invoice?.amount || invoice?.rawItem?.total_amount || 0).replace(/,/g, "")) || 0;
    const currentInvId = Number(invoice?.rawItem?.invoice_id || invoice?.id || invoice?.invoice_id || 0);

    // If viewing an already mapped or allocated invoice, filter prevInvoices to only those up to the current invoice
    const relevantPrevInvoices = (prevInvoices || []).filter((inv) => {
      if ((isAllocated || isMapped) && currentInvId) {
        const invId = Number(inv.invoice_id || inv.id || 0);
        return invId ? invId <= currentInvId : true;
      }
      return true;
    });

    // Map previous invoices fetched via API
    const previousRows = relevantPrevInvoices.map((inv, idx) => {
      const rawInvDate = inv.invoiced_date || inv.created_at || inv.date;
      let formattedInvoiceDate = "—";
      if (rawInvDate) {
        try {
          const d = new Date(rawInvDate);
          formattedInvoiceDate = isNaN(d.getTime()) ? rawInvDate : format(d, "dd MMM yyyy");
        } catch {
          formattedInvoiceDate = rawInvDate;
        }
      }

      const rawPayDate = inv.payment_date;
      let formattedPaymentDate = "—";
      if (rawPayDate) {
        try {
          const d = new Date(rawPayDate);
          formattedPaymentDate = isNaN(d.getTime()) ? rawPayDate : format(d, "dd MMM yyyy");
        } catch {
          formattedPaymentDate = rawPayDate;
        }
      }

      const rawAmt = inv.total_amount ?? inv.amount ?? 0;
      const numAmt = Number(String(rawAmt).replace(/,/g, "")) || 0;
      const rawStatus = inv.status || "mapped";
      const formattedStatus = rawStatus.charAt(0).toUpperCase() + rawStatus.slice(1).toLowerCase();

      const invNum = inv.invoice_number || `INV-${inv.invoice_id}` || "";
      const invRawSource = String(inv.source || "").toUpperCase();
      const isInvManual = invRawSource === "MANUAL" || invRawSource === "MANINV" || invRawSource === "CASH" || invNum.startsWith("MAN");
      const invSource = isInvManual ? "MANUAL" : (inv.source || "TT");

      return {
        id: `receipt-prev-${inv.invoice_id || idx}`,
        invoice_id: inv.invoice_id,
        invoice: invNum,
        source: invSource,
        date: formattedInvoiceDate,
        payment_date: formattedPaymentDate,
        amount: numAmt.toLocaleString("en-IN", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
        rawAmount: numAmt,
        status: formattedStatus,
        isCurrent: false,
      };
    });

    const existingIndex = previousRows.findIndex(
      (r) =>
        (currentInvId && r.invoice_id === currentInvId) ||
        (currentInvoiceNumber && r.invoice === currentInvoiceNumber)
    );

    if (existingIndex !== -1) {
      previousRows[existingIndex] = {
        ...previousRows[existingIndex],
        isCurrent: true,
      };
      return previousRows;
    }

    const currentRow = {
      id: "receipt-current",
      invoice: currentInvoiceNumber,
      source: currentSource,
      date: displayInvoiceDate,
      payment_date: displayPaymentDate,
      amount: currentAmountFmt,
      rawAmount: currentNumAmt,
      status: "this one",
      isCurrent: true,
    };

    return [...previousRows, currentRow];
  }, [invoice, displayInvoiceDate, displayPaymentDate, displayAmount, prevInvoices, isAllocated, isMapped]);

  const contractBudget = useMemo(() => {
    const rawVal =
      invoice?.project?.total_budget ??
      invoice?.rawItem?.project?.total_budget ??
      invoice?.contract_budget ??
      invoice?.total_budget ??
      matchedProjectObj?.total_budget ??
      matchedProjectObj?.budget ??
      matchedProjectObj?.contract_value ??
      matchedProjectObj?.budget_amount ??
      prevInvoicesSummary?.budget_amount ??
      0;
    return Number(String(rawVal).replace(/,/g, "")) || 0;
  }, [invoice, matchedProjectObj, prevInvoicesSummary]);

  const totalReceivedAmount = useMemo(() => {
    return fixedCostRows.reduce((sum, r) => sum + (r.rawAmount || 0), 0);
  }, [fixedCostRows]);

  const pendingAmount = useMemo(() => {
    if (isAllocated || isMapped || !prevInvoicesSummary?.pending_amount) {
      return Math.max(0, contractBudget - totalReceivedAmount);
    }
    return Number(prevInvoicesSummary.pending_amount) || 0;
  }, [isAllocated, isMapped, prevInvoicesSummary, contractBudget, totalReceivedAmount]);

  const handleSave = async () => {
    if (!selectedProject) {
      toast.warning("Please select a project before saving.");
      return;
    }

    // Manual invoices are already assigned to project_id during creation via /manual-invoice/create
    if (isManual) {
      toast.success(
        `Manual invoice ${invoice?.invoice || ""} is mapped to ${matchedProjectObj?.title || selectedProject} successfully!`
      );
      if (onSaveMapping) {
        onSaveMapping({
          project: selectedProject,
          scope: mapScope,
        });
      }
      onHide();
      return;
    }

    const invId =
      invoice?.invoice_id ||
      invoice?.rawItem?.invoice_id ||
      invoice?.rawItem?.id ||
      invoice?.id;
    if (!invId) {
      toast.error("Invoice ID is missing.");
      return;
    }

    try {
      const payload = {
        invoice_id: Number(invId),
        project_id: Number(selectedProject),
      };
      console.log("Mapping fixed cost invoice with payload:", payload);
      const res = await fixedCostMapInvoice(payload).unwrap();
      toast.success(res?.message || `Invoice ${invoice?.invoice || ""} mapped successfully!`);
      if (onSaveMapping) {
        onSaveMapping({
          project: selectedProject,
          scope: mapScope,
          data: res?.data,
        });
      }
      onHide();
    } catch (err) {
      console.error("Fixed cost mapping error:", err);
      toast.error(err?.data?.message || err?.message || "Failed to map invoice");
    }
  };

  const isModelLocked = Boolean(
    invoice?.budget_type ||
    invoice?.model === "Fixed" ||
    invoice?.status?.toLowerCase() === "mapped" ||
    invoice?.status?.toLowerCase() === "allocated"
  );

  const renderHeader = () => (
    <div className="d-flex align-items-center gap-2 flex-wrap">
      {isInline && (
        <button
          type="button"
          className="btn btn-sm border p-1 rounded-circle me-2 d-flex align-items-center justify-content-center"
          style={{
            width: "32px",
            height: "32px",
            backgroundColor: isDarkMode ? "#2a2a2a" : "#f8f9fa",
            color: isDarkMode ? "#f2f1f1" : "#475569",
            borderColor: isDarkMode ? "#444" : "#d1d5db",
          }}
          onClick={onHide}
          title="Back to Invoices"
        >
          <IoIosArrowBack size={20} />
        </button>
      )}
      <span className="fs-6 fw-bold m-0">
        {invoice?.status?.toLowerCase() === "mapped" || invoice?.status?.toLowerCase() === "allocated"
          ? `Mapped Details · ${invoice?.invoice || invoice?.invoice_number}`
          : `Map ${invoice?.invoice || invoice?.invoice_number}`} · ₹{displayAmount}
      </span>
      {isManual ? (
        <span className="badge-source-manual">MANUAL</span>
      ) : (
        <span className="badge bg-light text-dark border">
          {invoice?.source || "TT"}
        </span>
      )}
      <span className="badge-model-fixed">
        Fixed Cost
      </span>
    </div>
  );

  const renderBody = () => (
    <>
      {/* Top Banner */}
      <div
        className="alert alert-info py-2 px-3 small border-0 mb-3"
        style={{ backgroundColor: "#eff6ff", color: "#1e40af", borderRadius: "8px" }}
      >
        Mapping this invoice sets it to <strong>Mapped</strong>. Resource allocation for this project happens once, when the project is marked complete.
      </div>

      {/* Model Switcher Toolbar - only shown if model is not locked */}
      {!isModelLocked && (
        <div className="d-flex align-items-center justify-content-between p-2 bg-light border rounded mb-3">
          <span className="small text-muted fw-semibold">SELECT PROJECT MODEL:</span>
          <div className="btn-group btn-group-sm">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={onSwitchToContract}
            >
              Contract (Auto-Allocate)
            </button>
            <button
              type="button"
              className="btn btn-primary"
            >
              Fixed Cost (Scope &amp; Completion)
            </button>
          </div>
        </div>
      )}

      {/* Project and Scope Selectors */}
      <div className="row g-3 mb-3">
        <div className="col-12 col-md-6">
          <label className="form-label small fw-semibold text-muted mb-1 d-block">PROJECT</label>
          <ProjectSelectAutocomplete
            id="fixed_project_id"
            projects={fixedProjects.length > 0 ? fixedProjects : projectList}
            value={selectedProject}
            onChange={(newId) => handleProjectSelect(newId)}
            disabled={isProjectLocked}
            placeholder="Select and search project..."
            existingProjectsMap={existingProjectsMap}
            isDarkMode={isDarkMode}
          />
        </div>

        <div className="col-4 col-md-2">
          <label className="form-label small fw-semibold text-muted mb-1">INVOICE DATE</label>
          <div
            className="form-control"
            style={{
              display: "flex",
              alignItems: "center",
              backgroundColor: isDarkMode ? "#1e1e1e" : "#f8f9fa",
              color: isDarkMode ? "#f2f1f1" : "#1e293b",
              height: "38px",
              borderRadius: "4px",
              borderColor: isDarkMode ? "#444" : "#d1d5db",
              fontSize: "13px",
            }}
          >
            {displayInvoiceDate}
          </div>
        </div>
        <div className="col-4 col-md-2">
          <label className="form-label small fw-semibold text-muted mb-1">AMOUNT</label>
          <div
            className="form-control"
            style={{
              display: "flex",
              alignItems: "center",
              backgroundColor: isDarkMode ? "#1e1e1e" : "#f8f9fa",
              color: isDarkMode ? "#f2f1f1" : "#1e293b",
              height: "38px",
              borderRadius: "4px",
              borderColor: isDarkMode ? "#444" : "#d1d5db",
              fontVariantNumeric: "tabular-nums",
              fontWeight: 600,
              fontSize: "13px",
            }}
          >
            ₹{displayAmount}
          </div>
        </div>
        <div className="col-4 col-md-2">
          <label className="form-label small fw-semibold text-muted mb-1">PAYMENT RECEIVED</label>
          <div
            className="form-control"
            style={{
              display: "flex",
              alignItems: "center",
              backgroundColor: isDarkMode ? "#1e1e1e" : "#f8f9fa",
              color: isDarkMode ? "#f2f1f1" : "#1e293b",
              height: "38px",
              borderRadius: "4px",
              borderColor: isDarkMode ? "#444" : "#d1d5db",
              fontSize: "13px",
            }}
          >
            {displayPaymentDate}
          </div>
        </div>
      </div>

      <div className="d-flex align-items-center gap-2 mb-2">
        <span
          style={{
            width: "8px",
            height: "8px",
            borderRadius: "50%",
            backgroundColor: "#10b981",
            display: "inline-block",
          }}
        />
        <span className="fw-semibold" style={{ fontSize: "14px" }}>
          Receipts mapped to this project so far
        </span>
      </div>

      {/* Receipts on this project table (MUI DataGrid identical to Invoice Queue table) */}
      <Box
        sx={{
          width: "100%",
          mt: 2,
          mb: 3,
          height: fixedCostRows.length > 4 ? 295 : "auto",
          backgroundColor: isDarkMode ? "transparent" : "#fff",
          border: isDarkMode ? "1px solid #444" : "1px solid #e0e0e0",
          borderRadius: "6px",
          overflow: "hidden",
        }}
      >
        <DataGrid
          rows={fixedCostRows}
          columns={fixedCostColumns}
          autoHeight={fixedCostRows.length <= 4}
          columnHeaderHeight={52}
          rowHeight={48}
          hideFooterPagination
          disableRowSelectionOnClick
          disableColumnMenu
          getRowId={(row) => row.id}
          slots={{
            footer: () => (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  borderTop: isDarkMode ? "1px solid #333" : "1px solid #e0e0e0",
                  backgroundColor: isDarkMode ? "#1e1e1e" : "#f8fafc",
                  minHeight: "48px",
                  px: 0,
                  width: "100%",
                  fontWeight: 700,
                  fontSize: "13px",
                  color: isDarkMode ? "#f2f1f1" : "#1e293b",
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 390,
                    fontWeight: 700,
                    fontSize: "13px",
                    color: isDarkMode ? "#f2f1f1" : "#1e293b",
                    pl: "16px",
                  }}
                >
                  Total received
                </Box>
                <Box
                  sx={{
                    width: 160,
                    minWidth: 160,
                    maxWidth: 160,
                    flexShrink: 0,
                    textAlign: "right",
                    pr: "10px",
                    fontWeight: 700,
                    fontSize: "13px",
                    color: isDarkMode ? "#f2f1f1" : "#111827",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {totalReceivedAmount.toLocaleString("en-IN", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </Box>
                <Box
                  sx={{
                    width: 140,
                    minWidth: 140,
                    maxWidth: 140,
                    flexShrink: 0,
                  }}
                />
              </Box>
            ),
            noRowsOverlay: CustomNoRowsOverlay,
          }}
          sx={{
            border: "none !important",
            borderRadius: 0,
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: isDarkMode ? "#1e1e1e" : "#fff",
              borderBottom: isDarkMode ? "1px solid #333" : "1px solid #e0e0e0",
              fontSize: "13px",
              fontWeight: 600,
              color: isDarkMode ? "#f2f1f1" : "#6b7280",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: 600,
            },
            "& .MuiDataGrid-cell": {
              fontSize: "13px",
              borderBottom: isDarkMode ? "1px solid #2a2a2a" : "1px solid #f0f0f0",
              display: "flex",
              alignItems: "center",
            },
            "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
              outline: "none !important",
            },
            "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within": {
              outline: "none !important",
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: isDarkMode ? "#2a2a2a !important" : "#fafafa",
            },
            "& .MuiDataGrid-footerContainer": {
              borderTop: isDarkMode ? "1px solid #333" : "1px solid #e0e0e0",
              minHeight: "48px",
              backgroundColor: isDarkMode ? "#1e1e1e" : "transparent",
            },
            "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
              width: "6px",
              height: "6px",
            },
            "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-track": {
              backgroundColor: isDarkMode ? "#1e1e1e" : "#f8fafc",
            },
            "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb": {
              backgroundColor: isDarkMode ? "#555" : "#cbd5e1",
              borderRadius: "4px",
            },
            "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb:hover": {
              backgroundColor: isDarkMode ? "#777" : "#94a3b8",
            },
          }}
        />
      </Box>

      {/* Scope KPI Cards from Mockup Screen 3 */}
      <div className="row g-2 mb-3">
        <div className="col">
          <div className={`p-2 border rounded text-center h-100 ${isDarkMode ? "bg-dark border-secondary text-white" : "bg-light"}`}>
            <div className="text-muted small" style={{ fontSize: "10px" }}>
              CONTRACT VALUE
            </div>
            <div className="fw-bold" style={{ color: isDarkMode ? "#f2f1f1" : "inherit" }}>
              ₹{contractBudget.toLocaleString("en-IN")}
            </div>
          </div>
        </div>
        <div className="col">
          <div className={`p-2 border rounded text-center h-100 ${isDarkMode ? "bg-dark border-secondary text-white" : "bg-light"}`}>
            <div className="text-muted small" style={{ fontSize: "10px" }}>
              RECEIVED SO FAR
            </div>
            <div className="fw-bold text-success">
              ₹{totalReceivedAmount.toLocaleString("en-IN", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </div>
          </div>
        </div>
        <div className="col">
          <div className={`p-2 border rounded text-center h-100 ${isDarkMode ? "bg-dark border-secondary text-white" : "bg-light"}`}>
            <div className="text-muted small" style={{ fontSize: "10px" }}>
              PENDING AMOUNT
            </div>
            <div className="fw-bold text-warning">
              ₹{pendingAmount.toLocaleString("en-IN", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </div>
          </div>
        </div>
        <div className="col">
          <div className={`p-2 border rounded text-center h-100 ${isDarkMode ? "bg-dark border-secondary text-white" : "bg-light"}`}>
            <div className="text-muted small" style={{ fontSize: "10px" }}>
              PROJECT STATUS
            </div>
            <div className={`fw-bold ${contractBudget > 0 && totalReceivedAmount >= contractBudget ? "text-success" : (isDarkMode ? "text-info" : "text-primary")}`}>
              {contractBudget > 0 && totalReceivedAmount >= contractBudget ? "Completed" : (matchedProjectObj?.status || "In Progress")}
            </div>
          </div>
        </div>
      </div>

      {!isAllocated && (
        <div className="text-muted small mb-2" style={{ fontSize: "12px" }}>
          The Allocate button is enabled only when the project is marked complete. Until then this project contributes cost, but no earnings, to the monthly dashboard.
        </div>
      )}
    </>
  );

  const renderFooter = () => {
    if (isAllocated) return null;

    return (
      <>
        <button
          type="button"
          className="re-btn-cancel"
          onClick={onHide}
        >
          Cancel
        </button>

        <div className="d-flex align-items-center gap-2">
          {(invoice?.status === "Mapped" || invoice?.status?.toLowerCase() === "mapped") && (
            <button
              type="button"
              className="re-btn-sync"
              onClick={() => {
                if (onOpenFinalSplit) {
                  const contractBudgetVal = Number(
                    prevInvoicesSummary?.budget_amount != null
                      ? prevInvoicesSummary.budget_amount
                      : matchedProjectObj?.budget || matchedProjectObj?.total_budget || matchedProjectObj?.contract_value || 0
                  );
                  const pendingAmtVal = Number(
                    prevInvoicesSummary?.pending_amount != null
                      ? prevInvoicesSummary.pending_amount
                      : Math.max(0, contractBudgetVal - totalReceivedAmount)
                  );
                  onOpenFinalSplit({
                    project_id: selectedProject,
                    project_name: matchedProjectObj?.title || invoice?.project_name || invoice?.project || "",
                    total_received: totalReceivedAmount,
                    received_amount: totalReceivedAmount,
                    receipts_count: fixedCostRows.length,
                    pooled_receipts: fixedCostRows,
                    prev_invoices: prevInvoices,
                    prev_invoices_summary: prevInvoicesSummary,
                    cost_to_date: projectCostToDate,
                    contract_budget: contractBudgetVal,
                    pending_amount: pendingAmtVal,
                  });
                }
              }}
            >
              Allocate — Project Complete
            </button>
          )}
          {!(invoice?.status?.toLowerCase() === "mapped") && (
            <button
              type="button"
              className="re-btn-manual"
              disabled={!selectedProject || isMappingFixed}
              onClick={handleSave}
            >
              {isMappingFixed ? "Saving..." : "Save Mapping"}
            </button>
          )}
        </div>
      </>
    );
  };

  if (isInline) {
    return (
      <div className="card shadow-sm border mb-3" style={{ borderRadius: "8px" }}>
        <div className={`card-header ${isDarkMode ? "bg-dark text-white border-secondary" : "bg-white"} py-3 px-4 border-bottom d-flex align-items-center`}>
          {renderHeader()}
        </div>
        <div className={`card-body ${isDarkMode ? "bg-dark text-white" : ""} p-4`}>
          {renderBody()}
        </div>
        {!isAllocated && (
          <div className={`card-footer ${isDarkMode ? "bg-dark text-white border-secondary" : "bg-light"} py-3 px-4 d-flex align-items-center justify-content-between border-top`}>
            {renderFooter()}
          </div>
        )}
      </div>
    );
  }

  return (
    <Modal show={show} onHide={onHide} centered size="xl">
      <Modal.Header closeButton className={isDarkMode ? "bg-dark text-white border-secondary" : ""}>
        {renderHeader()}
      </Modal.Header>
      <Modal.Body className={`p-4 ${isDarkMode ? "bg-dark text-white" : ""}`}>
        {renderBody()}
      </Modal.Body>
      {!isAllocated && (
        <Modal.Footer className={`d-flex align-items-center justify-content-between ${isDarkMode ? "bg-dark text-white border-secondary" : "bg-light"}`}>
          {renderFooter()}
        </Modal.Footer>
      )}
    </Modal>
  );
};

export default FixedCostMappingModal;
